/**
 * The host's way in from the door: the 3, 2, 1 when the live is starting, then the live's own start and the meeting's
 * join. The computer's door and the Season studio both press it.
 */
import { useEffect, useRef, useState } from "react";
import type { MeetingState } from "./meeting";

/** The 3, 2, 1 before the host goes live. */
const COUNT_STEP_MS = 850;

export function useDoorEntry(
  phase: MeetingState["phase"],
  countdown: boolean,
  onEnter: () => void | Promise<unknown>,
): { count: number | null; busy: boolean; go: () => void } {
  const [count, setCount] = useState<number | null>(null);
  // Why its own flag: between the count and the meeting's own "joining" the page is still starting the live, and a
  // button that looks ready gets pressed twice.
  const [starting, setStarting] = useState(false);
  const step = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(step.current), []);

  const enter = () => {
    setStarting(true);
    void Promise.resolve(onEnter()).finally(() => setStarting(false));
  };

  // From the press to the room: the count, starting the live, joining. The button says so and cannot be pressed.
  const busy = count !== null || starting || phase === "joining";

  const go = () => {
    if (busy) return;
    if (!countdown) {
      enter();
      return;
    }
    let n = 3;
    setCount(n);
    const tick = () => {
      n -= 1;
      if (n > 0) {
        setCount(n);
        step.current = window.setTimeout(tick, COUNT_STEP_MS);
        return;
      }
      setCount(null);
      enter();
    };
    step.current = window.setTimeout(tick, COUNT_STEP_MS);
  };

  return { count, busy, go };
}
